/**
 * Ownership events — the structured acquisitions / divestitures / absorptions
 * that justify parent organizations being an entity at all.
 *
 * Written on change, one event at a time, exactly like the timeline section
 * beside it: add, edit and delete each persist immediately. A section that
 * buffered behind the form's own Save is how a curator's timeline was once lost,
 * so this one never does.
 *
 * IMAGE BYTES follow the timeline hero's order: the document is written FIRST,
 * then any file it no longer references is deleted — and only under this
 * organization's own `ownership-events/` prefix, so removing an event can never
 * delete someone else's picture. `TimelineImageSlot` itself never deletes.
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { TimelineEventImage } from '@watchkeeper/shared-types';
import type { ParentOrganization, ParentOrgOwnershipEvent } from '../../types';
import {
  deleteCalibreImage,
  getManufacturerCatalogue,
  PARENT_ORGANIZATIONS_COLLECTION,
  updateParentOrganization,
  uploadParentOrganizationOwnershipImage,
} from '../../services/firebase';
import { fetchBrandListing, isAlgoliaConfigured } from '../../services/algolia';
import { useBrandSearch } from '../../hooks/useBrandSearch';
import { withoutUndefined } from '../../utils/withoutUndefined';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { TimelineImageSlot } from '../Timeline/TimelineImageSlot';
import { EntityPicker, optionMatches, type EntityOption } from './EntityPicker';
import { OwnershipEnrichDialog } from './OwnershipEnrichDialog';
import { QuickOrgResolve } from './QuickOrgResolve';
import type { ResolveCatalogues } from './ownershipEventResolve';

type EventType = ParentOrgOwnershipEvent['type'];
type TargetType = ParentOrgOwnershipEvent['targetType'];

/**
 * Every event type, with the words and the colour it is shown in.
 *
 * THIS IS THE REGISTRY — `EVENT_TYPES` is derived from its keys, so the
 * compiler refuses a build that leaves a type out. The list used to be a
 * hand-written `EventType[]`, which type-checks perfectly while silently
 * missing a member of the union: widening the shared type would have left the
 * new types unreachable from the dropdown with nothing to say so. Same fix as
 * the AI Prompts render list (#586) — derive the list, never restate it.
 *
 * The label matters because the chip used to render `{event.type}` verbatim,
 * which turns `acquired_by` into "ACQUIRED_BY".
 *
 * `tone` says whose deal it was, which is the distinction the two new types
 * exist to draw:
 * - `actor`   — this organization did it (indigo)
 * - `subject` — it was done to this organization (amber)
 * - `self`    — about the organization itself, no counterparty (slate)
 */
const EVENT_TYPE_LABELS: Record<EventType, { label: string; tone: 'actor' | 'subject' | 'self' }> = {
  acquired: { label: 'Acquired', tone: 'actor' },
  divested: { label: 'Divested', tone: 'actor' },
  acquired_by: { label: 'Acquired by', tone: 'subject' },
  divested_from: { label: 'Divested from', tone: 'subject' },
  founded: { label: 'Founded', tone: 'actor' },
  absorbed: { label: 'Absorbed', tone: 'actor' },
  renamed: { label: 'Renamed', tone: 'self' },
  other: { label: 'Other', tone: 'self' },
};

/** Derived, never restated — see {@link EVENT_TYPE_LABELS}. */
const EVENT_TYPES = Object.keys(EVENT_TYPE_LABELS) as EventType[];

const TONE_CLASSES: Record<'actor' | 'subject' | 'self', string> = {
  actor: 'bg-indigo-50 text-indigo-700',
  subject: 'bg-amber-50 text-amber-800',
  self: 'bg-slate-100 text-slate-600',
};

/**
 * The chip for an event type. Falls back to the raw value for a type stored
 * before the registry knew about it, rather than rendering blank.
 */
export function eventTypeChip(type: EventType): { label: string; className: string } {
  const entry = EVENT_TYPE_LABELS[type];
  return {
    label: entry?.label ?? type,
    className: TONE_CLASSES[entry?.tone ?? 'self'],
  };
}

/**
 * Which of the two parties `targetId` holds, in words, for the editor's label.
 *
 * `targetId` is always the OTHER party except on the two self types — so on
 * `acquired_by` it is the buyer, and a curator told "what it was about" would
 * reasonably put this organization there instead.
 */
const TARGET_PROMPTS: Partial<Record<EventType, string>> = {
  acquired_by: 'Who acquired this organization',
  divested_from: 'Which group it was divested from',
  founded: 'What was founded (this organization, unless it founded a subsidiary)',
  renamed: 'What was renamed',
};

/** Types whose target is this organization itself, so the editor pre-fills it. */
const SELF_TARGET_TYPES: ReadonlySet<EventType> = new Set<EventType>(['founded', 'renamed']);

/** Types whose counterparty is always another organization, never a brand. */
const ORG_COUNTERPARTY_TYPES: ReadonlySet<EventType> = new Set<EventType>([
  'acquired_by',
  'divested_from',
]);

const TARGET_TYPES: Array<{ value: TargetType; label: string }> = [
  { value: 'brand', label: 'Brand' },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'parentOrg', label: 'Parent organization' },
];

/** First four-digit year in a free-form date, for ordering; undated sorts last. */
function yearOf(date: string): number {
  const match = /\b(\d{4})\b/.exec(date || '');
  return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
}

/** A fresh id for a new event — stable within the array, which is all it needs. */
function newEventId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `evt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

interface Draft {
  id: string;
  date: string;
  type: EventType;
  targetType: TargetType;
  targetId: string;
  previousOwnerId: string;
  description: string;
  sourceUrl: string;
  /** Text, so a blank field can mean "absent" rather than 0. */
  aiConfidence: string;
  image?: TimelineEventImage;
}

function toDraft(event?: ParentOrgOwnershipEvent): Draft {
  return {
    id: event?.id ?? newEventId(),
    date: event?.date ?? '',
    type: event?.type ?? 'acquired',
    targetType: event?.targetType ?? 'brand',
    targetId: event?.targetId ?? '',
    previousOwnerId: event?.previousOwnerId ?? '',
    description: event?.description ?? '',
    sourceUrl: event?.sourceUrl ?? '',
    aiConfidence: typeof event?.aiConfidence === 'number' ? String(event.aiConfidence) : '',
    image: event?.image,
  };
}

/**
 * The draft after the curator changes the event type.
 *
 * Extracted because it is the part of the editor that can be quietly wrong:
 * the two families of type pull `targetId` in opposite directions, and the
 * difference is invisible once it is stored.
 *
 * - `founded` / `renamed` are about the organization itself, so the target IS
 *   this organization — pre-filled, but never over an existing pick.
 * - `acquired_by` / `divested_from` are the opposite: the target is the OTHER
 *   organization, so a target left over from an outbound type (a brand that was
 *   acquired, say) is wrong and is cleared rather than silently re-pointed.
 */
export function applyTypeChange(draft: Draft, type: EventType, orgId: string): Draft {
  if (SELF_TARGET_TYPES.has(type) && !draft.targetId) {
    return { ...draft, type, targetType: 'parentOrg', targetId: orgId };
  }
  if (ORG_COUNTERPARTY_TYPES.has(type) && draft.targetType !== 'parentOrg') {
    return { ...draft, type, targetType: 'parentOrg', targetId: '' };
  }
  // An inbound type whose target is already this organization is the mistake
  // the label warns about — the curator picked "what it was about" before
  // changing the type. Clear it rather than storing a self-referential event.
  if (ORG_COUNTERPARTY_TYPES.has(type) && draft.targetId === orgId) {
    return { ...draft, type, targetId: '' };
  }
  return { ...draft, type };
}

/** Draft → stored event, with no undefined anywhere Firestore would reject. */
export function draftToEvent(draft: Draft): ParentOrgOwnershipEvent {
  const confidence = draft.aiConfidence.trim() === '' ? undefined : Number(draft.aiConfidence);
  return withoutUndefined({
    id: draft.id,
    date: draft.date.trim(),
    type: draft.type,
    targetType: draft.targetType,
    targetId: draft.targetId,
    previousOwnerId: draft.previousOwnerId || undefined,
    description: draft.description.trim() || undefined,
    sourceUrl: draft.sourceUrl.trim() || undefined,
    aiConfidence:
      confidence !== undefined && Number.isFinite(confidence)
        ? Math.min(1, Math.max(0, confidence))
        : undefined,
    image: draft.image ? withoutUndefined(draft.image) : undefined,
  });
}

interface Props {
  org: ParentOrganization;
  /** Every parent organization — the previous-owner and parent-org target options. */
  orgs: ParentOrganization[];
  onOrgChange?: (next: ParentOrganization) => void;
  /** The enrich review created an organization or added an alias — reload the list. */
  onOrgsChanged?: () => void;
}

export function OwnershipEventsSection({ org, orgs, onOrgChange, onOrgsChanged }: Props) {
  const events = useMemo(
    () => [...(org.ownershipEvents ?? [])].sort((a, b) => yearOf(a.date) - yearOf(b.date)),
    [org.ownershipEvents],
  );

  const [brands, setBrands] = useState<Array<{ id: string; name?: string; displayName?: string }>>([]);
  const [manufacturers, setManufacturers] = useState<EntityOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  // Loaded once for the section: the rows need the names to label their targets,
  // not just the editor. Both are single index requests.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      isAlgoliaConfigured() ? fetchBrandListing() : Promise.resolve([]),
      getManufacturerCatalogue(),
    ])
      .then(([brandHits, manufacturerEntries]) => {
        if (cancelled) return;
        setBrands(
          brandHits.map((h) => ({ id: h.objectID, name: h.name, displayName: h.displayName })),
        );
        setManufacturers(
          manufacturerEntries.map((m) => ({
            id: m.id,
            label: m.displayName || m.name,
            sub: m.country,
          })),
        );
      })
      .catch((err) => {
        console.error('Failed to load ownership-event target options:', err);
        if (!cancelled) toast.error('Could not load brands and manufacturers for the pickers.');
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Organizations created from inside this section, overlaid on the list the
   * parent handed down.
   *
   * A freshly created organization has to be selectable IMMEDIATELY. The parent
   * reloads on `onOrgsChanged`, and the parent-org index is about to be an
   * Algolia one — neither is read-after-write, so a picker that re-queried
   * would show "No matches" for the company the curator just made. Overlaying
   * the returned document locally is the same trick the enrich review already
   * uses, and the same freshness fix the manufacturer catalogue needed.
   */
  const [sessionOrgs, setSessionOrgs] = useState<Record<string, ParentOrganization>>({});
  const effectiveOrgs = useMemo(() => {
    const created = Object.values(sessionOrgs);
    if (created.length === 0) return orgs;
    const byId = new Map(orgs.map((o) => [o.id, o]));
    for (const next of created) byId.set(next.id, next);
    return [...byId.values()];
  }, [orgs, sessionOrgs]);

  /** Remember a created organization, and let the parent refresh in its own time. */
  const rememberOrg = (next: ParentOrganization) => {
    setSessionOrgs((prev) => ({ ...prev, [next.id]: next }));
    onOrgsChanged?.();
  };

  const orgOptions: EntityOption[] = useMemo(
    () =>
      effectiveOrgs.map((o) => ({ id: o.id, label: o.displayName || o.name || o.id, sub: o.country })),
    [effectiveOrgs],
  );
  const brandOptions: EntityOption[] = useMemo(
    () => brands.map((b) => ({ id: b.id, label: b.displayName || b.name || b.id })),
    [brands],
  );

  const labelFor = (targetType: TargetType, id: string): string => {
    const pool =
      targetType === 'brand' ? brandOptions : targetType === 'manufacturer' ? manufacturers : orgOptions;
    return pool.find((o) => o.id === id)?.label ?? id;
  };

  const [enrichOpen, setEnrichOpen] = useState(false);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [deleting, setDeleting] = useState<ParentOrgOwnershipEvent | null>(null);
  const [busy, setBusy] = useState(false);

  const imagePrefix = `${PARENT_ORGANIZATIONS_COLLECTION}/${org.id}/ownership-events/`;

  /**
   * What the AI dialog resolves proposed NAMES against — the same three pools
   * the manual pickers use, so a name the curator could pick by hand is a name
   * the resolver can match on its own.
   */
  const catalogues: ResolveCatalogues = useMemo(
    () => ({
      brand: brands,
      manufacturer: manufacturers.map((m) => ({ id: m.id, name: m.label })),
      parentOrg: effectiveOrgs,
    }),
    [brands, manufacturers, effectiveOrgs],
  );
  const pickerOptions = useMemo(
    () => ({ brand: brandOptions, manufacturer: manufacturers, parentOrg: orgOptions }),
    [brandOptions, manufacturers, orgOptions],
  );

  /** Write the whole array, then drop bytes nothing references any more. */
  const persist = async (next: ParentOrgOwnershipEvent[], staleImagePaths: string[]) => {
    await updateParentOrganization(org.id, { ownershipEvents: next });
    onOrgChange?.({ ...org, ownershipEvents: next });
    const stillUsed = new Set(next.map((e) => e.image?.storagePath).filter(Boolean));
    for (const path of staleImagePaths) {
      if (!path || stillUsed.has(path) || !path.startsWith(imagePrefix)) continue;
      try {
        await deleteCalibreImage(path);
      } catch (err) {
        console.warn('Ownership-event image delete failed (non-fatal):', err);
      }
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.date.trim()) {
      toast.error('Give the event a date — a year, or "circa 1988".');
      return;
    }
    if (!editing.targetId) {
      toast.error('Choose what the event is about.');
      return;
    }
    const event = draftToEvent(editing);
    const current = org.ownershipEvents ?? [];
    const previous = current.find((e) => e.id === event.id);
    const next = previous ? current.map((e) => (e.id === event.id ? event : e)) : [...current, event];
    setBusy(true);
    try {
      await persist(next, previous?.image?.storagePath ? [previous.image.storagePath] : []);
      toast.success(previous ? 'Event updated' : 'Event added');
      setEditing(null);
    } catch (err) {
      console.error('Failed to save ownership event:', err);
      toast.error('Could not save that event.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    const next = (org.ownershipEvents ?? []).filter((e) => e.id !== deleting.id);
    setBusy(true);
    try {
      await persist(next, deleting.image?.storagePath ? [deleting.image.storagePath] : []);
      toast.success('Event deleted');
      setDeleting(null);
    } catch (err) {
      console.error('Failed to delete ownership event:', err);
      toast.error('Could not delete that event.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Ownership events</h3>
        <span className="text-[11px] text-gray-500">
          {events.length} event{events.length === 1 ? '' : 's'}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setEnrichOpen(true)}
            disabled={busy || optionsLoading}
            title="Propose acquisitions and divestitures with AI, match each to the catalogue, then apply"
          >
            ✨ AI Enrich Ownership Events
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setEditing(toDraft())} disabled={busy}>
            + Add event
          </Button>
        </div>
      </div>
      <p className="mt-1 text-[11px] text-gray-500">
        Acquisitions, divestitures and absorptions, each pointing at the brand, manufacturer or
        organization involved. Saves immediately.
      </p>

      {events.length === 0 ? (
        <p className="mt-3 text-xs text-gray-400">No ownership events yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {events.map((event) => (
            <li key={event.id} className="flex items-start gap-3 px-3 py-2 text-sm">
              <span className="w-24 shrink-0 font-mono text-xs text-gray-600">{event.date}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-1.5">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                      eventTypeChip(event.type).className
                    }`}
                  >
                    {eventTypeChip(event.type).label}
                  </span>
                  <span className="text-gray-800">{labelFor(event.targetType, event.targetId)}</span>
                  <span className="text-[10px] text-gray-400">({event.targetType})</span>
                  {event.previousOwnerId && (
                    <span className="text-[11px] text-gray-500">
                      from {labelFor('parentOrg', event.previousOwnerId)}
                    </span>
                  )}
                </div>
                {event.description && (
                  <p className="mt-0.5 text-xs text-gray-600 line-clamp-2">{event.description}</p>
                )}
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  className="text-xs text-indigo-600 hover:underline disabled:text-gray-300"
                  disabled={busy}
                  onClick={() => setEditing(toDraft(event))}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="text-xs text-red-600 hover:underline disabled:text-gray-300"
                  disabled={busy}
                  onClick={() => setDeleting(event)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <OwnershipEventEditor
          draft={editing}
          onChange={setEditing}
          org={org}
          orgs={effectiveOrgs}
          orgOptions={orgOptions}
          onOrgCreated={rememberOrg}
          brands={brands}
          manufacturerOptions={manufacturers}
          optionsLoading={optionsLoading}
          labelFor={labelFor}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSave={() => void handleSave()}
          uploadImage={(file, fileName) =>
            uploadParentOrganizationOwnershipImage(org.id, file, fileName)
          }
        />
      )}

      <OwnershipEnrichDialog
        isOpen={enrichOpen}
        org={org}
        catalogues={catalogues}
        options={pickerOptions}
        orgs={effectiveOrgs}
        onOrgsChanged={onOrgsChanged}
        onClose={() => setEnrichOpen(false)}
        onApply={async (events) => {
          // Appended, never replacing: the dialog only ever offers events that
          // are not already recorded, and a curator's own entries are untouched.
          await persist([...(org.ownershipEvents ?? []), ...events], []);
        }}
      />

      <Modal
        isOpen={deleting !== null}
        onClose={() => (busy ? undefined : setDeleting(null))}
        title="Delete ownership event"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Delete the {deleting?.type} event dated{' '}
            <span className="font-medium">{deleting?.date}</span>? This saves immediately.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleting(null)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void handleDelete()} loading={busy}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function OwnershipEventEditor({
  draft,
  onChange,
  org,
  orgs,
  orgOptions,
  onOrgCreated,
  brands,
  manufacturerOptions,
  optionsLoading,
  labelFor,
  busy,
  onCancel,
  onSave,
  uploadImage,
}: {
  draft: Draft;
  onChange: (next: Draft) => void;
  org: ParentOrganization;
  /** Full documents — what the inline create needs to spot an id collision. */
  orgs: ParentOrganization[];
  orgOptions: EntityOption[];
  /** An organization was minted from a picker; make it selectable at once. */
  onOrgCreated: (next: ParentOrganization) => void;
  brands: Array<{ id: string; name?: string; displayName?: string }>;
  manufacturerOptions: EntityOption[];
  optionsLoading: boolean;
  labelFor: (targetType: TargetType, id: string) => string;
  busy: boolean;
  onCancel: () => void;
  onSave: () => void;
  uploadImage: (file: File | Blob, fileName?: string) => Promise<string>;
}) {
  const [targetQuery, setTargetQuery] = useState('');
  const [ownerQuery, setOwnerQuery] = useState('');
  // Brands search through the house hook (typo-tolerant); the other two pools
  // are small enough to match locally.
  const { results: brandResults } = useBrandSearch(brands, targetQuery);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => onChange({ ...draft, [key]: value });

  const targetOptions: EntityOption[] =
    draft.targetType === 'brand'
      ? brandResults.map((b) => ({ id: b.id, label: b.displayName || b.name || b.id }))
      : (draft.targetType === 'manufacturer' ? manufacturerOptions : orgOptions).filter((o) =>
          optionMatches(o, targetQuery),
        );
  // A previous owner is some OTHER organization.
  const ownerOptions = orgOptions.filter((o) => o.id !== org.id && optionMatches(o, ownerQuery));

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500';

  return (
    <Modal
      isOpen
      onClose={() => (busy ? undefined : onCancel())}
      title={draft.targetId || draft.date ? 'Edit ownership event' : 'Add ownership event'}
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Date</label>
            <input
              type="text"
              value={draft.date}
              aria-label="Event date"
              onChange={(e) => set('date', e.target.value)}
              placeholder='1999, or "circa 1988"'
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Type</label>
            <select
              value={draft.type}
              aria-label="Event type"
              onChange={(e) => {
                setTargetQuery('');
                onChange(applyTypeChange(draft, e.target.value as EventType, org.id));
              }}
              className={`${inputClass} bg-white`}
            >
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {EVENT_TYPE_LABELS[t].label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {TARGET_PROMPTS[draft.type] ?? 'What it was about'}
          </label>
          {ORG_COUNTERPARTY_TYPES.has(draft.type) && (
            <p className="mb-1.5 text-[11px] text-amber-800">
              This event happened TO {org.displayName || org.name || org.id} — name the other
              organization here, not this one.
            </p>
          )}
          <div className="mb-2 flex gap-3">
            {TARGET_TYPES.map((t) => (
              <label key={t.value} className="flex items-center gap-1.5 text-sm text-gray-700">
                <input
                  type="radio"
                  name="ownership-target-type"
                  checked={draft.targetType === t.value}
                  onChange={() => {
                    setTargetQuery('');
                    onChange({ ...draft, targetType: t.value, targetId: '' });
                  }}
                />
                {t.label}
              </label>
            ))}
          </div>
          <EntityPicker
            value={draft.targetId}
            selectedLabel={draft.targetId ? labelFor(draft.targetType, draft.targetId) : undefined}
            options={targetOptions}
            query={targetQuery}
            onQueryChange={setTargetQuery}
            onSelect={(id) => set('targetId', id)}
            ariaLabel="Event target"
            placeholder={`Search ${TARGET_TYPES.find((t) => t.value === draft.targetType)?.label.toLowerCase()}s…`}
            loading={optionsLoading && draft.targetType !== 'parentOrg'}
          />
          {/* Parent-org targets only, and only once the curator has typed
              something to name it after. A brand or manufacturer target is
              picked from its own catalogue — minting one here would let a
              mistyped brand become a parent organization. */}
          {draft.targetType === 'parentOrg' && !draft.targetId && targetQuery.trim() && (
            <QuickOrgResolve
              name={targetQuery}
              orgs={orgs}
              disabled={busy}
              ariaContext="the event target"
              onResolved={(next) => {
                onOrgCreated(next);
                setTargetQuery('');
                onChange({ ...draft, targetType: 'parentOrg', targetId: next.id });
              }}
            />
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Previous owner <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <EntityPicker
            value={draft.previousOwnerId}
            selectedLabel={
              draft.previousOwnerId ? labelFor('parentOrg', draft.previousOwnerId) : undefined
            }
            options={ownerOptions}
            query={ownerQuery}
            onQueryChange={setOwnerQuery}
            onSelect={(id) => set('previousOwnerId', id)}
            ariaLabel="Previous owner"
            placeholder="Search parent organizations…"
            allowClear
          />
          {!draft.previousOwnerId && ownerQuery.trim() && (
            <QuickOrgResolve
              name={ownerQuery}
              orgs={orgs}
              disabled={busy}
              ariaContext="the previous owner"
              onResolved={(next) => {
                onOrgCreated(next);
                setOwnerQuery('');
                onChange({ ...draft, previousOwnerId: next.id });
              }}
            />
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
          <textarea
            value={draft.description}
            aria-label="Event description"
            onChange={(e) => set('description', e.target.value)}
            rows={3}
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Source URL</label>
            <input
              type="url"
              value={draft.sourceUrl}
              aria-label="Source URL"
              onChange={(e) => set('sourceUrl', e.target.value)}
              placeholder="https://"
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              AI confidence <span className="font-normal text-gray-400">(0–1)</span>
            </label>
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={draft.aiConfidence}
              aria-label="AI confidence"
              onChange={(e) => set('aiConfidence', e.target.value)}
              placeholder="blank = curator"
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Image</label>
          <TimelineImageSlot
            value={draft.image}
            onChange={(image) => set('image', image)}
            uploadImage={uploadImage}
            disabled={busy}
            label="ownership event image"
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onSave} loading={busy}>
            Save event
          </Button>
        </div>
      </div>
    </Modal>
  );
}
